/**
 * The work in each project by state (status.md, "Work states"): its checkouts by what their
 * session is doing, and its landings by where they are in the pipeline. One state system, so
 * queued, verifying, landed, failed, running and waiting on you read the same as everywhere.
 * Pure.
 */
import type { Machine, Pipeline, PipelineItem, Project } from '@orgus/protocol';
import { WORK_STATES } from '@orgus/ui';
import type { RunOutcome, WorkState } from '@orgus/ui';
import type { CheckoutRow } from './model.ts';

/** Finished landings older than this drop out of the project's groups and counts. */
export const RECENT_MS = 24 * 3600_000;

export type WorkItem =
  | { kind: 'checkout'; key: string; state: WorkState; row: CheckoutRow }
  | { kind: 'landing'; key: string; state: WorkState; item: PipelineItem; pipeline: Pipeline };

export interface ProjectWork {
  id: string;
  project: Project | undefined;
  rows: CheckoutRow[];
  machines: Machine[];
  pipelines: Pipeline[];
  items: WorkItem[];
  counts: Record<WorkState, number>;
  /** Finished landing runs oldest first, then what verifies now: the project's history strip. */
  runs: RunOutcome[];
  lastLanded: number | null;
}

/** A checkout's state: its session waits on you, git failed, its session works, or it is idle. */
export function checkoutState(r: Pick<CheckoutRow, 'checkout' | 'session'>): WorkState {
  if (r.session?.status === 'waiting') return 'needs-you';
  if (r.checkout.error) return 'failed';
  if (r.session?.status === 'working') return 'running';
  return 'stale';
}

/** A landing's state, or null for one withdrawn (cancelled): it is no work anyone waits on. */
export function landingState(i: Pick<PipelineItem, 'state'>): WorkState | null {
  switch (i.state) {
    case 'queued':
      return 'queued';
    case 'active':
      return 'verifying';
    case 'passed':
      return 'landed';
    case 'failed':
      return 'failed';
    default:
      return null;
  }
}

/** A submission title's count of commits beyond the first: "(and 2 more)", "(+2)". */
const MORE = /\s*\((?:and \d+ more|\+\d+)\)\s*$/;

/**
 * Two submissions of the same change: the same title once the count of further commits is
 * dropped. A retry often carries one commit more or fewer ("(+2)" then "(+1)"), and read as
 * another change its earlier failure stood as failed after the retry landed (23 Oct).
 */
export function sameChange(a: string, b: string): boolean {
  return a === b || a.replace(MORE, '') === b.replace(MORE, '');
}

const zero = (): Record<WorkState, number> => Object.fromEntries(WORK_STATES.map((s) => [s, 0])) as Record<WorkState, number>;

/** Every project's work, the busiest and most urgent first. */
export function projectWork(rows: readonly CheckoutRow[], pipelines: readonly Pipeline[], projects: Record<string, Project>, now: number): ProjectWork[] {
  const ids = [...new Set([...rows.map((r) => r.checkout.projectId), ...pipelines.map((p) => p.projectId)])].filter((id) => rows.some((r) => r.checkout.projectId === id));
  const out = ids.map((id): ProjectWork => {
    const mine = rows.filter((r) => r.checkout.projectId === id);
    const pls = pipelines.filter((p) => p.projectId === id);
    const items: WorkItem[] = mine.map((row) => ({ kind: 'checkout', key: `c:${row.checkout.id}`, state: checkoutState(row), row }));
    for (const p of pls) {
      const all = [...p.items, ...p.recent];
      for (const item of all) {
        const state = landingState(item);
        if (!state) continue;
        if ((state === 'landed' || state === 'failed') && now - (item.endedAt ?? item.startedAt ?? 0) > RECENT_MS) continue;
        // A failed attempt that was submitted again is history, not work: its newer attempt stands for it.
        if (state === 'failed' && all.some((o) => o !== item && sameChange(o.title, item.title) && (o.startedAt ?? 0) > (item.startedAt ?? 0))) continue;
        items.push({ kind: 'landing', key: `l:${p.machineId}:${item.id}`, state, item, pipeline: p });
      }
    }
    const counts = zero();
    for (const i of items) counts[i.state] += 1;
    const done = pls
      .flatMap((p) => p.recent)
      .filter((i) => i.state === 'passed' || i.state === 'failed' || i.state === 'cancelled')
      .sort((a, b) => (a.endedAt ?? 0) - (b.endedAt ?? 0));
    const verifying = pls.flatMap((p) => p.items).filter((i) => i.state === 'active').length;
    const landed = done.filter((i) => i.state === 'passed');
    return {
      id,
      project: projects[id],
      rows: mine,
      machines: [...new Map(mine.filter((r) => r.machine).map((r) => [r.machine!.id, r.machine!])).values()],
      pipelines: pls,
      items,
      counts,
      runs: [...done.map((i) => i.state as RunOutcome), ...Array.from({ length: verifying }, (): RunOutcome => 'running')],
      lastLanded: landed.length ? (landed[landed.length - 1]!.endedAt ?? null) : null,
    };
  });
  return out.sort(
    (a, b) =>
      b.counts['needs-you'] - a.counts['needs-you'] ||
      b.counts.failed - a.counts.failed ||
      b.counts.running - a.counts.running ||
      b.counts.verifying + b.counts.queued - (a.counts.verifying + a.counts.queued) ||
      (a.project?.name ?? '').localeCompare(b.project?.name ?? ''),
  );
}

/** Items in one state, newest work first within it (oldest queued first: that is the queue's order). */
export function itemsIn(items: readonly WorkItem[], state: WorkState): WorkItem[] {
  const at = (i: WorkItem) => (i.kind === 'landing' ? (i.item.endedAt ?? i.item.stageStartedAt ?? i.item.startedAt ?? 0) : (i.row.session?.lastActivity ?? i.row.checkout.updatedAt));
  const list = items.filter((i) => i.state === state);
  // Checkouts before landings in a shared state (failed), then by time.
  return list.sort((a, b) => (a.kind === b.kind ? (state === 'queued' ? at(a) - at(b) : at(b) - at(a)) : a.kind === 'checkout' ? -1 : 1));
}
